import {
    IconActivity,
    IconAreaChart,
    IconBell,
    IconCompany,
    IconGrid,
    IconHome,
    IconLoader,
    IconMessageCircle,
    IconModule1On1,
    IconModule360,
    IconModuleAutomations,
    IconModuleObjectives,
    IconModulePraise,
    IconModuleRequestFeedback,
    IconModuleReviews,
    IconSettings,
    IconTeam,
} from 'featherico';
import { Observable } from 'ladda-react';
import { negate, overSome, stubTrue } from 'lodash/fp';
import * as React from 'react';
import { Feature, UserCompanyDto } from '../../../../../types/CompanyDtos';
import NotificationCountDto from '../../../../../types/NotificationCountDto';
import SparkMetaDto from '../../../../../types/SparkMetaDto';
import { CurrentUserDto } from '../../../../../types/UserDtos';
import api from '../../../../api';
import * as R from '../../../../routes';
import { SparkVisibility } from '../../../components/Spark/content';
import { NAVIGATION } from '../../../intercom/intercom-data-targets';
import { isFeatureEnabled, isFeatureEnabledForUser } from '../../../services/Features';
import r from '../../../services/Replacement';
import {
    hasBasicHr,
    hasManager,
    isAdmin,
    isAnyAdmin,
    isCycleAdminFor,
    isGuest,
    isHr,
    isManager,
} from '../../../services/User';
import { countAdminErrors } from '../../Admin/Overview/service';
import { getRetentionFeatureMenuName } from '../../Retention/wording';
import { showGeneralFeedbackWording } from '../../YourFeedback/services/feedback';

const featureStatus =
    (...features: Feature[]) =>
    (enabledFeatures: UserCompanyDto['enabledFeatures']) =>
        features.some(feature => enabledFeatures[feature]);

interface Link {
    title: string;
    route: string;
    alsoMatch: string[];
    icon: React.ReactNode;
    /** For admins false will give faded-out appearance, for others will filter out the link */
    isFeatureEnabled: (enabledFeatures: UserCompanyDto['enabledFeatures']) => boolean;
    /** Determine whether the link is visible in the navigation */
    isAvailableTo: (currentUser: CurrentUserDto) => boolean;
    notifier: null | Notifier;
    titleClass: string;
    trackingLabel: string;
    /** When set, admins will see a pillbox beside the link.  */
    getStatusLabel?: (currentUser: CurrentUserDto) => Promise<string | undefined>;
}

interface Notifier<T = unknown[]> {
    observer: () => Observable<T>;
    toNotification: (r: T) => { count: number; color: string };
}

interface Group {
    className: string;
    links: Link[];
}

export const employeeLinks = [
    {
        className: '',
        links: [
            {
                title: 'Home',
                route: R.BASE + R.DASHBOARD,
                alsoMatch: [],
                icon: <IconHome />,
                isFeatureEnabled: stubTrue,
                isAvailableTo: negate(isGuest),
                notifier: null,
                titleClass: '',
                trackingLabel: 'home',
            },
            {
                title: 'Inbox',
                route: R.BASE + R.INBOX,
                alsoMatch: [],
                icon: <IconBell />,
                isFeatureEnabled: stubTrue,
                isAvailableTo: user => !isGuest(user) && isFeatureEnabledForUser('FEATURE_inboxPage', user),
                notifier: {
                    observe: () => api.notificationsCount.getCount.createObservable(),
                    toNotification: ({ count }: NotificationCountDto) => ({
                        count,
                        color: 'RED',
                    }),
                },
                titleClass: '',
                trackingLabel: 'inbox',
            },
            {
                title: r('praise.menu.title'),
                route: R.BASE + R.PRAISE_STREAM,
                alsoMatch: [],
                icon: <IconModulePraise />,
                isFeatureEnabled: featureStatus('praise'),
                isAvailableTo: negate(isGuest),
                notifier: null,
                titleClass: '',
                trackingLabel: 'praise',
                dataIntercomTarget: NAVIGATION.EMPLOYEE_PRAISE,
            },
            {
                title: r('meeting.name.plural'),
                route: R.BASE + R.MEETING_OVERVIEW,
                alsoMatch: [R.BASE + R.MEETING_DEDICATED],
                icon: <IconModule1On1 />,
                isFeatureEnabled: featureStatus('meetings'),
                isAvailableTo: negate(isGuest),
                notifier: null,
                titleClass: '',
                trackingLabel: 'meetings',
                dataIntercomTarget: NAVIGATION.EMPLOYEE_MEETINGS,
            },
        ],
    },
    {
        links: [
            {
                title: isFeatureEnabled('FEATURE_layoutRefresh') ? r('surveys.plural.big') : r('surveys.menu'),
                route: R.BASE + R.YOUR_SURVEYS,
                alsoMatch: [],
                icon: <IconActivity />,
                isFeatureEnabled: f => f.surveys,
                isAvailableTo: user => (user.isGuest ? false : isFeatureEnabledForUser('surveys', user)),
                notifier: null,
                trackingLabel: 'surveys',
                dataIntercomTarget: NAVIGATION.EMPLOYEE_SURVEYS,
            },
            {
                title: `${isFeatureEnabled('FEATURE_layoutRefresh') ? '' : 'Your '}${r('objective.plural.title')}`,
                route: R.BASE + R.OBJECTIVE_BROWSER,
                alsoMatch: [],
                icon: <IconModuleObjectives />,
                isFeatureEnabled: featureStatus('objective'),
                isAvailableTo: negate(isGuest),
                notifier: null,
                titleClass: '',
                trackingLabel: 'objectives',
                dataIntercomTarget: NAVIGATION.EMPLOYEE_OBJECTIVES,
            },
            {
                title: `${isFeatureEnabled('FEATURE_layoutRefresh') ? '' : 'Your '}${showGeneralFeedbackWording ? r('fr.menu.unified.title') : r('fr.menu.title')}`,
                route: R.BASE + R.FEEDBACK_USER,
                alsoMatch: [],
                icon: <IconModule360 />,
                isFeatureEnabled: featureStatus('feedback', 'requestFeedback'),
                isAvailableTo: stubTrue,
                notifier: null,
                titleClass: '',
                trackingLabel: '360s',
                dataIntercomTarget: NAVIGATION.EMPLOYEE_FEEDBACK,
            },
            {
                title: `${isFeatureEnabled('FEATURE_layoutRefresh') ? '' : 'Your '}${r('pr.menu.title')}`,
                route: R.BASE + R.YOUR_REVIEWS,
                alsoMatch: ['/appraisalView', '/appraisalDetails'],
                icon: <IconModuleReviews />,
                isFeatureEnabled: featureStatus('review'),
                isAvailableTo: negate(isGuest),
                notifier: null,
                titleClass: '',
                trackingLabel: 'reviews',
                dataIntercomTarget: NAVIGATION.EMPLOYEE_REVIEWS,
            },
        ],
    },
    {
        className: '',
        links: [
            {
                title: isFeatureEnabled('FEATURE_layoutRefresh') ? 'Automations' : 'Your Automations',
                route: R.BASE + R.EMPLOYEE_AUTOMATIONS,
                alsoMatch: [],
                icon: <IconModuleAutomations />,
                isFeatureEnabled: featureStatus('meetings'), // TODO: When we add more automations this needs to check if any actions/triggers are available
                isAvailableTo: u => !isGuest(u) && isFeatureEnabledForUser('FEATURE_automationsEmployee', u),
                notifier: null,
                titleClass: '',
                trackingLabel: 'employeeAutomations',
            },
            {
                title: 'Team',
                route: R.BASE + R.YOUR_TEAM,
                alsoMatch: [],
                icon: <IconTeam />,
                isFeatureEnabled: stubTrue,
                isAvailableTo: u =>
                    !isGuest(u) && (isManager(u) || hasManager(u)) && !isFeatureEnabled('FEATURE_layoutRefresh'),
                notifier: null,
                titleClass: '',
                trackingLabel: 'team',
            },
            {
                title: r('company.big'),
                route: R.BASE + R.YOUR_COMPANY,
                alsoMatch: [],
                icon: <IconCompany />,
                isFeatureEnabled: stubTrue,
                isAvailableTo: u => !isGuest(u) && !isFeatureEnabled('FEATURE_layoutRefresh'),
                notifier: null,
                titleClass: '',
                trackingLabel: 'company',
            },
            {
                title: 'People',
                route: R.BASE + R.PEOPLE,
                alsoMatch: [],
                icon: <IconTeam />,
                isFeatureEnabled: stubTrue,
                isAvailableTo: u => !isGuest(u) && isFeatureEnabled('FEATURE_layoutRefresh'),
                notifier: null,
                titleClass: '',
                trackingLabel: 'people',
            },
        ],
    },
] as Group[];

export const adminLinks = (highlightComingSoon: boolean) =>
    [
        {
            links: [
                {
                    title: 'Home',
                    route: R.BASE + R.ADMIN_DASHBOARD,
                    alsoMatch: [],
                    icon: <IconHome />,
                    isFeatureEnabled: stubTrue,
                    isAvailableTo: hasBasicHr,
                    notifier: null,
                    titleClass: '',
                    trackingLabel: 'adminDashboard',
                },
                {
                    title: r('praise.singular'),
                    route: R.BASE + R.ADMIN_PRAISE,
                    alsoMatch: [],
                    icon: <IconModulePraise />,
                    isFeatureEnabled: featureStatus('praise'),
                    isAvailableTo: hasBasicHr,
                    notifier: null,
                    titleClass: '',
                    trackingLabel: 'adminPraise',
                    dataIntercomTarget: NAVIGATION.ADMIN_PRAISE,
                },
                {
                    title: r('meeting.name.plural'),
                    route: R.BASE + R.ADMIN_MEETINGS,
                    alsoMatch: [],
                    icon: <IconModule1On1 />,
                    isFeatureEnabled: featureStatus('meetings'),
                    isAvailableTo: hasBasicHr,
                    notifier: null,
                    titleClass: '',
                    trackingLabel: 'adminMeetings',
                    dataIntercomTarget: NAVIGATION.ADMIN_MEETINGS,
                },
                {
                    title: `${r('surveys.plural.big')}`,
                    route: R.BASE + R.ADMIN_SURVEYS,
                    alsoMatch: [],
                    icon: <IconActivity />,
                    isFeatureEnabled: f => f.surveys,
                    isAvailableTo: user => isFeatureEnabledForUser('surveys', user) && hasBasicHr(user),
                    notifier: null,
                    trackingLabel: 'adminSurveys',
                    dataIntercomTarget: NAVIGATION.ADMIN_SURVEYS,
                },
                {
                    title: r('objective.plural.title'),
                    route: R.BASE + R.ADMIN_OBJECTIVES_SELECT_CYCLE,
                    alsoMatch: [],
                    icon: <IconModuleObjectives />,
                    isFeatureEnabled: featureStatus('objective'),
                    isAvailableTo: user => hasBasicHr(user) || isCycleAdminFor(user, 'OBJECTIVES'),
                    notifier: null,
                    titleClass: '',
                    trackingLabel: 'adminObjectives',
                    dataIntercomTarget: NAVIGATION.ADMIN_OBJECTIVES,
                },
                {
                    title: r('fr.menu.title'),
                    route: R.BASE + R.ADMIN_FEEDBACK_SELECT_CYCLE,
                    alsoMatch: [],
                    icon: <IconModule360 />,
                    isFeatureEnabled: featureStatus('feedback'),
                    isAvailableTo: user => hasBasicHr(user) || isCycleAdminFor(user, '360_FEEDBACK'),
                    notifier: null,
                    titleClass: '',
                    trackingLabel: 'admin360s',
                    dataIntercomTarget: NAVIGATION.ADMIN_360_FEEDBACK,
                },
                {
                    title: isFeatureEnabled('FEATURE_anytimeFeedbackGive') ? r('anyfb.name') : 'Feedback Requests',
                    route: R.BASE + R.ADMIN_FEEDBACK_REQUESTED,
                    alsoMatch: [],
                    icon: <IconModuleRequestFeedback />,
                    isFeatureEnabled: featureStatus('requestFeedback'),
                    isAvailableTo: hasBasicHr,
                    notifier: {
                        observe: () =>
                            api.sparks.getOne.createObservable(
                                'ANYTIME_FEEDBACK_ADMIN_AUTOMATIONS',
                                'notify',
                                SparkVisibility.ANNOUNCEMENT
                            ),
                        toNotification: (spark: SparkMetaDto) => ({
                            count: spark?.status === 'ACTIVE' ? 1 : null,
                            color: 'YELLOW',
                        }),
                    },
                    titleClass: '',
                    trackingLabel: 'adminRequestedFeedback',
                    dataIntercomTarget: NAVIGATION.ADMIN_ANYTIME_FEEDBACK,
                },
                {
                    title: r('pr.menu.title'),
                    route: R.BASE + R.ADMIN_REVIEWS_SELECT_CYCLE,
                    alsoMatch: [],
                    icon: <IconModuleReviews />,
                    isFeatureEnabled: featureStatus('review'),
                    isAvailableTo: user => hasBasicHr(user) || isCycleAdminFor(user, 'REVIEWS'),
                    notifier: null,
                    titleClass: '',
                    trackingLabel: 'adminReviews',
                    dataIntercomTarget: NAVIGATION.ADMIN_REVIEWS,
                },
                {
                    title: r('messages.menu.title'),
                    route: R.BASE + R.ADMIN_MESSAGES,
                    alsoMatch: [],
                    icon: <IconMessageCircle />,
                    isFeatureEnabled: featureStatus('message'),
                    isAvailableTo: u => hasBasicHr(u) && isFeatureEnabledForUser('message', u),
                    notifier: null,
                    titleClass: '',
                    trackingLabel: 'adminMessages',
                    dataIntercomTarget: NAVIGATION.ADMIN_MESSAGES,
                },
                {
                    title: getRetentionFeatureMenuName(),
                    route: R.BASE + R.INSIGHTS,
                    alsoMatch: [],
                    icon: <IconAreaChart />,
                    isFeatureEnabled: f => f.retention,
                    isAvailableTo: u => isFeatureEnabledForUser('retention', u) && isHr(u),
                    notifier: null,
                    titleClass: '',
                    trackingLabel: 'adminRetentionInsights',
                },
                {
                    title: 'Automations',
                    route: R.BASE + R.HR_AUTOMATIONS,
                    alsoMatch: [],
                    icon: <IconModuleAutomations />,
                    isFeatureEnabled: stubTrue,
                    isAvailableTo: u => isFeatureEnabledForUser('FEATURE_automationsHrMenu', u) && hasBasicHr(u),
                    notifier: null,
                    titleClass: '',
                    trackingLabel: 'adminAutomations',
                },
            ],
        },
        {
            className: '',
            links: [
                {
                    title: 'Directory',
                    route: R.BASE + R.ADMIN_USERS_DIRECTORY,
                    alsoMatch: [],
                    icon: <IconCompany />,
                    isFeatureEnabled: stubTrue,
                    isAvailableTo: isAnyAdmin,
                    notifier: null,
                    titleClass: '',
                    trackingLabel: 'adminDirectory',
                },
                {
                    title: 'Integrations',
                    route: R.BASE + R.INTEGRATIONS,
                    alsoMatch: [],
                    icon: <IconGrid />,
                    isFeatureEnabled: stubTrue,
                    isAvailableTo: overSome([isHr, isAdmin]),
                    notifier: {
                        observe: api.admin.getIntegrationsLinks.createObservable,
                        toNotification: links => ({ count: countAdminErrors(links), color: 'RED' }),
                    },
                    titleClass: '',
                    trackingLabel: 'adminIntegrations',
                },
                {
                    title: 'Settings',
                    route: R.BASE + R.ADMIN_OVERVIEW,
                    alsoMatch: ['/admin'],
                    icon: <IconSettings />,
                    isFeatureEnabled: stubTrue,
                    isAvailableTo: isAnyAdmin,
                    notifier: {
                        observe: api.admin.getSettingsLinks.createObservable,
                        toNotification: links => ({ count: countAdminErrors(links), color: 'RED' }),
                    },
                    titleClass: '',
                    trackingLabel: 'adminSettings',
                },
                {
                    title: 'Coming Soon',
                    route: R.BASE + R.ADMIN_EARLY_ACCESS,
                    alsoMatch: [],
                    icon: <IconLoader />,
                    isFeatureEnabled: stubTrue,
                    isAvailableTo: u => {
                        return isAnyAdmin(u) && isFeatureEnabledForUser('FEATURE_earlyAccessProgram', u);
                    },
                    titleClass: highlightComingSoon ? 'coming-soon' : '',
                    trackingLabel: 'adminSettings',
                },
            ],
        },
    ] as Group[];
